/**
 * Asking before a bulk review gesture on an action-plan group — MYAC-1215.
 *
 * "Accept all" fired immediately: one click accepted twelve tasks, and the way
 * that was found was a mis-click during a QA run. Un-accept all had asked since
 * 4.8a; accept all had not, so the cheap gesture was the destructive one.
 *
 * Every bulk gesture on a group header now asks, except the two that already
 * open a dialog of their own: rejecting collects a comment, and accepting a set
 * holding parked rows raises the keep-or-revert prompt instead.
 *
 * The questions live here, as OPTIONS rather than as a component, so the two
 * branches that matter can be tested without mounting the screen: confirming
 * runs the bulk path, declining does nothing. Same reasoning as
 * `sweepConfirm.ts` (MYAC-1126), which this follows.
 */
import type { useConfirm } from 'primevue/useconfirm'

type Confirm = ReturnType<typeof useConfirm>
/** `useI18n`'s `t`, in the pluralising form these messages need. */
type Translate = (key: string, named?: Record<string, unknown>, plural?: number) => string

/**
 * Named so the bulk gestures' OWN dialog claims them and the app-wide one does
 * not.
 *
 * `App.vue` mounts an ungrouped `ConfirmDialog` that every other confirmation in
 * the product lands on, stretched to whatever width the page gives it. These two
 * ask a short question and want a compact box, so they get their own instance
 * next to it — mounted ONCE in the shell rather than per surface, because
 * `ActionPlanGroup` is rendered from three (the plan panel, the summary slice,
 * the executive summary) and a dialog mounted per surface is three chances to
 * forget one. The same fan-out `ReapproveModeDialog` is mounted in `App.vue` to
 * avoid.
 */
export const BULK_REVIEW_CONFIRM_GROUP = 'action-plan-bulk-review'

export interface BulkReviewConfirmArgs {
  t: Translate
  /** How many rows the gesture will actually move — see `movableRows`. */
  count: number
  onAccept: () => void
}

/**
 * The question a bulk ACCEPT is asked with.
 *
 * The count is the whole point: "3" and "40" are different decisions, and the
 * one that hurt was a mis-click on a group of twelve. It is passed twice —
 * once to interpolate, once to pluralise — as every count in this app is.
 */
export function acceptAllConfirmOptions({ t, count, onAccept }: BulkReviewConfirmArgs) {
  return {
    group: BULK_REVIEW_CONFIRM_GROUP,
    message: t('referentials.actionPlan.acceptAllConfirm', { count }, count),
    header: t('referentials.actionPlan.acceptAll'),
    // No icon. A tick beside a question reads as an answer already given, and
    // the header says which gesture is being confirmed. The warning triangle
    // stays on un-accept below, where there IS something to lose.
    rejectProps: { label: t('common.cancel'), severity: 'secondary' as const, outlined: true },
    // A different key from the button that opened this, for the same reason the
    // un-accept one has its own: the header already says "Accept all", and the
    // action button is answering the question, not repeating the title.
    acceptProps: { label: t('referentials.actionPlan.acceptAllAction'), severity: 'success' as const },
    accept: onAccept,
  }
}

/**
 * The question a bulk UN-ACCEPT is asked with — moved here from
 * `ActionPlanGroup` so both bulk questions share one group, and therefore one
 * shape. It was landing on the app-wide dialog, which is why it rendered as a
 * band across the whole screen for a single sentence.
 */
export function unacceptAllConfirmOptions({ t, count, onAccept }: BulkReviewConfirmArgs) {
  return {
    group: BULK_REVIEW_CONFIRM_GROUP,
    message: t('referentials.actionPlan.unacceptAllConfirm', { count }, count),
    header: t('referentials.actionPlan.unacceptAll'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary' as const, outlined: true },
    // The header's label is idiomatically "Annuler les acceptations" in French,
    // and the dialog's own cancel button is "Annuler" — reusing it would put two
    // buttons starting with the same word side by side, one of which does the
    // opposite.
    acceptProps: { label: t('referentials.actionPlan.unacceptAllAction'), severity: 'danger' as const },
    accept: onAccept,
  }
}

/**
 * The question a bulk UN-REJECT is asked with.
 *
 * It used to go straight through, and the reasoning was sound as far as it
 * went: un-rejecting removes a decision nobody was working from, so nothing is
 * lost. But "nothing is lost" is not "nothing happened" — a group of rejected
 * rows comes back onto the list waiting to be decided again, and that is a
 * change the user should have meant. Every bulk gesture on the header now asks
 * except the two that already open a dialog of their own.
 *
 * Not dressed as a warning: no icon, no danger colour. This one genuinely
 * costs nothing, and a red alert over a harmless act is how people learn to
 * click through the ones that are not.
 */
export function unrejectAllConfirmOptions({ t, count, onAccept }: BulkReviewConfirmArgs) {
  return {
    group: BULK_REVIEW_CONFIRM_GROUP,
    message: t('referentials.actionPlan.unrejectAllConfirm', { count }, count),
    header: t('referentials.actionPlan.unrejectAll'),
    rejectProps: { label: t('common.cancel'), severity: 'secondary' as const, outlined: true },
    // Its own key again: the header is "Annuler les rejets" in French, beside a
    // cancel button reading "Annuler".
    acceptProps: { label: t('referentials.actionPlan.unrejectAllAction') },
    accept: onAccept,
  }
}

/** Any of the three questions above. */
export type BulkReviewConfirmOptions =
  | ReturnType<typeof acceptAllConfirmOptions>
  | ReturnType<typeof unacceptAllConfirmOptions>
  | ReturnType<typeof unrejectAllConfirmOptions>

/** Ask, then run only if the answer was yes. */
export function askBeforeBulkReview(confirm: Confirm, options: BulkReviewConfirmOptions): void {
  confirm.require(options)
}
